import React from 'react';
import { FaChartLine, FaUsers, FaBullhorn, FaThumbsUp, FaArrowRight } from 'react-icons/fa';
import { motion } from 'framer-motion';

const Why = () => {
  const stats = [
    { value: '95%', label: 'Client Satisfaction' },
    { value: '150+', label: 'Happy Clients' },
    { value: '24/7', label: 'Dedicated Support' }
  ];

  const benefits = [
    {
      icon: <FaChartLine />,
      title: 'Grow Your Sales',
      description: 'Accelerate revenue with targeted strategies designed to attract and convert.'
    },
    {
      icon: <FaUsers />,
      title: 'Drive Insane Traffic',
      description: 'Skyrocket web visitors using organic, paid, and social campaigns.'
    },
    {
      icon: <FaBullhorn />,
      title: 'Convert Raving Fans',
      description: 'Turn visitors into evangelists with optimized funnels and remarketing.'
    },
    {
      icon: <FaThumbsUp />,
      title: 'Maximize ROI',
      description: 'Higher conversions, smarter spend, faster results—guaranteed.'
    }
  ];

  return (
    <section className="py-16 sm:py-20 px-6 sm:px-8 lg:px-12 bg-white min-h-screen flex items-center">
      <div className="max-w-7xl mx-auto w-full">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="inline-block bg-gradient-to-r from-orange-300 to-pink-300 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4 tracking-wider">
            OUR DIFFERENCE
          </span>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-gray-900 mb-6 tracking-tight">
            Why <span className="font-semibold">Choose Us</span>
          </h2>
          <p className="text-xl font-light text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Choose us only if you're ready to grow your sales, drive insane traffic, and convert visitors into raving fans.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-20 max-w-4xl mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ staggerChildren: 0.1 }}
          viewport={{ once: true }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white border border-gray-200 rounded-2xl p-8 text-center hover:shadow-xl transition-all"
            >
              <div className="text-4xl md:text-5xl font-light bg-gradient-to-r from-orange-400 via-pink-400 to-purple-400 bg-clip-text text-transparent mb-2">
                {stat.value}
              </div>
              <div className="text-gray-600 font-light">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Content Section */}
        <div className="flex flex-col lg:flex-row gap-12 items-center">
          {/* Image Column */}
          <motion.div
            className="w-full lg:w-1/2"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="relative rounded-3xl overflow-hidden border border-gray-200 shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
                alt="Cyborg Sapient team analyzing digital marketing strategy and data analytics"
                className="object-cover w-full h-full"
                loading="lazy"
              />
            </div>
          </motion.div>

          {/* Benefits Column */}
          <div className="w-full lg:w-1/2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-100 to-pink-100 flex items-center justify-center text-orange-400 mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:bg-gradient-to-r group-hover:from-orange-400 group-hover:via-pink-400 group-hover:to-purple-400 group-hover:bg-clip-text group-hover:text-transparent transition-all">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 font-light leading-relaxed">
                    {benefit.description}
                  </p>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              viewport={{ once: true }}
              className="text-center mt-12"
            >
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-orange-300 via-pink-300 to-purple-300 hover:opacity-90 text-white font-medium py-4 px-8 rounded-full transition-opacity shadow-lg"
              >
                Let's Grow Your Business Together!
                <FaArrowRight className="transition-transform group-hover:translate-x-1" />
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Why;